// FILE: src/pages/FAQPage.jsx
// Default export: FAQ page with accessible accordion UI and common questions.
import React, { useEffect, useState } from "react";
import { ChevronDownIcon } from "@heroicons/react/24/outline";

function AccordionItem({ id, title, children, isOpen, onToggle }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white">
      <button
        aria-expanded={isOpen}
        aria-controls={`panel-${id}`}
        onClick={onToggle}
        className="flex w-full items-center justify-between gap-4 px-4 py-4 text-left"
      >
        <span className="text-[15px] font-medium text-slate-800">{title}</span>
        <ChevronDownIcon
          className={`h-5 w-5 text-slate-500 transition-transform ${isOpen ? "rotate-180" : ""}`}
        />
      </button>

      <div
        id={`panel-${id}`}
        role="region"
        aria-labelledby={`btn-${id}`}
        className={`px-4 pb-5 text-sm leading-6 text-slate-700 ${isOpen ? "block" : "hidden"}`}
      >
        {children}
      </div>
    </div>
  );
}

export default function FAQPage() {
  const [openId, setOpenId] = useState(null);

  useEffect(() => {
    const fromHash = window.location.hash?.replace("#", "");
    if (fromHash) setOpenId(fromHash);
  }, []);

  const faqs = [
    {
      id: "ordering",
      q: "How do I place an order or book a Ticket?",
      a: (
        <p>
          Browse events, select your Ticket category, add to cart, and complete payment at checkout. You will receive a confirmation email (and SMS if provided) after successful payment.
        </p>
      ),
    },
    {
      id: "payment-methods",
      q: "What payment methods do you accept?",
      a: (
        <p>
          We accept major credit/debit cards, net banking, UPI, and popular wallets via our payment gateway partners. Payment options available at checkout depend on your region and the payment provider.
        </p>
      ),
    },
    {
      id: "tickets-delivery",
      q: "How will I receive my Tickets?",
      a: (
        <p>
          E-tickets are usually sent to your registered email. For some events, mobile/print-at-home tickets or physical tickets (if applicable) may be provided — details will be included in your booking confirmation.
        </p>
      ),
    },
    {
      id: "cancellation-refund",
      q: "What is your cancellation and refund policy?",
      a: (
        <p>
          Cancellation and refund rules vary by event and Ticket category. Generally, cancellation requests must be made within 7 days of order and may be subject to vendor/organiser approval. Please check our Cancellation & Refund Policy page or contact support for specific cases.
        </p>
      ),
    },
    {
      id: "shipping",
      q: "When will my order be shipped?",
      a: (
        <p>
          Orders are handed over to courier/postal services within 0–7 days of order/payment or as per the promised delivery date. Delivery times thereafter depend on the courier and destination. Check the Shipping & Delivery Policy for more details.
        </p>
      ),
    },
    {
      id: "changes-event",
      q: "The event was cancelled or rescheduled — what should I do?",
      a: (
        <p>
          Organisers typically decide refunds or rescheduling. We will notify affected customers by email and offer available options. For specific guidance, check the event update email or contact our support with your booking ID.
        </p>
      ),
    },
    {
      id: "seat-selection",
      q: "Can I change my seat after booking?",
      a: (
        <p>
          Seat changes depend on organiser and venue rules. Contact support with your booking details — we'll try to assist, but seat reassignments are not guaranteed.
        </p>
      ),
    },
    {
      id: "accessibility",
      q: "Are events accessible for people with disabilities?",
      a: (
        <p>
          Many organisers provide accessibility information on the event page. If you need special assistance, contact us or the organiser before the event so we can help coordinate accommodations where possible.
        </p>
      ),
    },
    {
      id: "lost-ticket",
      q: "I lost my Ticket — can I get a replacement?",
      a: (
        <p>
          If you lose your e-Ticket, locate your confirmation email or log into your account to re-download it. Contact support immediately with proof of purchase and ID — replacements depend on organiser policies.
        </p>
      ),
    },
    {
      id: "contact-support",
      q: "How can I contact customer support?",
      a: (
        <p>
          You can reach out via email at <a className="text-amber-600 underline" href="mailto:info@showtimebro.com">info@showtimebro.com</a> or call/WhatsApp <a href="tel:+919479839747" className="underline">+91 94798 39747</a>. For quicker help, include your booking ID and a brief description of the issue.
        </p>
      ),
    },
  ];

  return (
    <main className="min-h-screen bg-slate-50">
      <div className="border-b border-slate-200 bg-white">
        <div className="mx-auto max-w-6xl px-4 py-8">
          <h1 className="text-2xl font-semibold text-slate-900">Frequently Asked Questions (FAQ)</h1>
          <p className="mt-1 text-sm text-slate-600">Answers to common questions about booking, delivery, refunds, and more.</p>
        </div>
      </div>

      <div className="mx-auto max-w-6xl px-4 py-8">
        <div className="mb-6">
          <input
            type="search"
            placeholder="Search FAQs — e.g. refund, ticket, shipping"
            onChange={(e) => {
              const q = e.target.value.trim().toLowerCase();
              if (!q) return setOpenId(null);
              // attempt simple search: open first matching FAQ
              const found = faqs.find((f) => f.q.toLowerCase().includes(q) || (typeof f.a === 'string' && f.a.toLowerCase().includes(q)));
              if (found) setOpenId(found.id);
            }}
            className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 shadow-sm focus:outline-none focus:ring-1 focus:ring-amber-500"
          />
        </div>

        <div className="space-y-3">
          {faqs.map((f) => (
            <AccordionItem
              key={f.id}
              id={f.id}
              title={f.q}
              isOpen={openId === f.id}
              onToggle={() => setOpenId(openId === f.id ? null : f.id)}
            >
              {f.a}
            </AccordionItem>
          ))}
        </div>

        <p className="mt-8 text-xs text-slate-500">
          If you can't find what you're looking for, please contact our support at <a className="text-amber-600 underline" href="mailto:info@showtimebro.com">info@showtimebro.com</a> or call/WhatsApp <a href="tel:+917440550105" className="underline">+91 74405 50105</a> and include your booking ID where applicable.
        </p>
      </div>
    </main>
  );
}
